import React, { useRef, useCallback, useMemo } from "react";
import { Container, Sprite, Text } from "@pixi/react";
import * as PIXI from "pixi.js";
import { useTranslation } from "react-i18next";
import { createButtonPressAnimation } from "../../../../utilities";
import { getNewUITexture } from "../../../../assets";
import { useDimensions } from "../../../../Contexts/DimensionsContext";
import { USER_INTERFACE_BUTTON_CONFIG } from "../../../../pixiSettings";
import { SOUND_NAME } from "../../../../sounds";
import { soundManager } from "../../../../SoundManager";
import { FONT_CONFIG } from "../../../../pixiSettings";

const CheckboxWithInput = ({
  label,
  translationKey,
  checked = false,
  onChange,
  value = "",
  onValueChange,
  x = 0,
  y = 0,
  sizeMultiplier = 0.7,
  labelFontSize,
  inputWidth = 200, // Width of the input button
  inputHeight = 80, // Height of the input button
  containerWidth, // Total width available for the component (to align input to right)
  inputId, // Unique ID for this input
  focusedInput, // Currently focused input ID
  onFocusChange, // Callback to change focused input
}) => {
  const { t } = useTranslation();
  const { getResponsiveConfig } = useDimensions();

  // Use translation if translationKey is provided, otherwise use label prop
  const displayLabel = translationKey ? t(translationKey) : label;

  // Refs for sprites
  const checkboxBackgroundRef = useRef(null);
  const checkmarkSpriteRef = useRef(null);
  const inputButtonRef = useRef(null);

  // Check if this input is currently focused
  const isInputFocused = focusedInput === inputId;

  // Create brightness filter for hover effect
  const brightnessFilter = useRef(new PIXI.ColorMatrixFilter());
  brightnessFilter.current.brightness(1.3, false);

  const inputBrightnessFilter = useRef(new PIXI.ColorMatrixFilter());
  inputBrightnessFilter.current.brightness(2.0, false);

  // Use ref for hover state to avoid unnecessary re-renders
  const isCheckboxHoveredRef = useRef(false);
  const isInputHoveredRef = useRef(false);

  // Get the checkbox textures
  const checkboxBackgroundTexture = getNewUITexture(
    checked
      ? "checkbox_background_active"
      : "checkbox_background"
  );
  const checkmarkTexture = getNewUITexture("checkmark");
  const inputBackgroundTexture = getNewUITexture("autospin_settings_entervalue_background");

  // Get responsive configuration from context
  const buttonConfig = getResponsiveConfig(USER_INTERFACE_BUTTON_CONFIG, true);

  // Apply size multiplier to checkbox dimensions
  const checkboxSize = buttonConfig.width * sizeMultiplier;

  // Calculate label position (to the right of checkbox with some padding)
  const labelX = checkboxSize * 1.0;
  const labelY = 0;

  // Use the parent-computed perfect font size directly
  const finalLabelFontSize = labelFontSize || 26;

  // Create label style
  const labelStyle = useMemo(
    () =>
      new PIXI.TextStyle({
        fontFamily: FONT_CONFIG.PRIMARY,
        fontSize: finalLabelFontSize,
        fontWeight: "400",
        fill: 0xffffff,
        align: "left",
        wordWrap: false,
      }),
    [finalLabelFontSize]
  );

  /**
   * Auto-scaling text font size calculation for input field
   * Same logic as PopiBetButton - scales based on current display value
   */
  const inputFontSize = useMemo(() => {
    // Show cursor when focused
    const displayValue = isInputFocused ? (value || "") + "|" : value || "";

    // Ideal font size - 60% of button height
    const idealFontSize = Math.round(inputHeight * 0.6);

    // Allow 90% of button width for text (10% padding on sides)
    const maxTextWidth = inputWidth * 0.9;

    // Measure text width with ideal font size
    const tempStyle = new PIXI.TextStyle({
      fontFamily: FONT_CONFIG.PRIMARY,
      fontSize: idealFontSize,
      fontWeight: "400",
    });
    const metrics = PIXI.TextMetrics.measureText(displayValue, tempStyle);

    // Only scale down if text exceeds max width
    if (metrics.width > maxTextWidth) {
      const scale = maxTextWidth / metrics.width;
      return Math.round(idealFontSize * scale);
    }

    return idealFontSize;
  }, [value, isInputFocused, inputWidth, inputHeight]);

  // Input text style
  const inputTextStyle = useMemo(
    () =>
      new PIXI.TextStyle({
        fontFamily: FONT_CONFIG.PRIMARY,
        fontSize: inputFontSize,
        fontWeight: "400",
        fill: 0xffffff,
        align: "center",
      }),
    [inputFontSize]
  );

  // Calculate input button position
  // If containerWidth is provided, align to the right edge
  // Otherwise, position right after the label text
  const inputButtonX = useMemo(() => {
    if (containerWidth) {
      // Align to far right: containerWidth - half of input width (since anchor is 0.5)
      return containerWidth - inputWidth / 2;
    } else {
      // Original behavior: position after label text with same gap as checkbox-to-label
      const labelMetrics = PIXI.TextMetrics.measureText(
        displayLabel,
        labelStyle
      );
      const gapBetweenElements = checkboxSize * 0.75; // Same gap as checkbox to label
      return labelX + labelMetrics.width + gapBetweenElements + inputWidth / 2;
    }
  }, [
    containerWidth,
    inputWidth,
    displayLabel,
    labelStyle,
    labelX,
    checkboxSize,
  ]);

  // Handle checkbox click
  const handleCheckboxClick = useCallback(() => {
    soundManager.play(SOUND_NAME.button_click);

    if (checkboxBackgroundRef.current) {
      createButtonPressAnimation(checkboxBackgroundRef.current);
    }

    if (onChange) {
      const newChecked = !checked;

      // Call onChange to update checkbox state
      // The parent's onChange will handle clearing the value when unchecking
      onChange(newChecked);

      // If checking the checkbox (turning it on), focus the input
      if (newChecked && onFocusChange && inputId) {
        onFocusChange(inputId);

        // Focus the external HTML input to trigger mobile keyboard
        if (window.autoplayInputRefs) {
          const inputRef = window.autoplayInputRefs[inputId];
          if (inputRef?.current) {
            // Set focus maintenance flag BEFORE focusing (critical for iOS)
            if (window.autoplayInputRefs.shouldMaintainFocusRef) {
              window.autoplayInputRefs.shouldMaintainFocusRef.current = true;
            }
            // Claim focus for this tap so the tap-away dismissal (see
            // HiddenAutoplayInputs) doesn't blur the field we just focused.
            window.autoplayInputRefs.focusIntentThisTap = true;

            // iOS sometimes responds better to click() than focus()
            if (inputRef.current.click) {
              inputRef.current.click();
            }
            inputRef.current.focus();

            // Aggressive refocus strategy for iOS
            requestAnimationFrame(() => {
              if (
                inputRef?.current &&
                document.activeElement !== inputRef.current
              ) {
                inputRef.current.focus();
              }
            });

            setTimeout(() => {
              if (
                inputRef?.current &&
                document.activeElement !== inputRef.current
              ) {
                inputRef.current.focus();
              }
            }, 10);

            setTimeout(() => {
              if (
                inputRef?.current &&
                document.activeElement !== inputRef.current
              ) {
                inputRef.current.focus();
              }
            }, 50);

            setTimeout(() => {
              if (
                inputRef?.current &&
                document.activeElement !== inputRef.current
              ) {
                inputRef.current.focus();
              }
            }, 100);

            setTimeout(() => {
              if (
                inputRef?.current &&
                document.activeElement !== inputRef.current
              ) {
                inputRef.current.focus();
              }
            }, 200);

            setTimeout(() => {
              if (
                inputRef?.current &&
                document.activeElement !== inputRef.current
              ) {
                inputRef.current.focus();
              }
            }, 300);
          }
        }
      }
    }
  }, [checked, onChange, onFocusChange, inputId]);

  // Handle input button click - toggle editing mode
  const handleInputClick = useCallback(
    (e) => {
      // Prevent event propagation to avoid conflicts
      if (e && e.stopPropagation) {
        e.stopPropagation();
      }

      soundManager.play(SOUND_NAME.button_click);

      if (inputButtonRef.current) {
        createButtonPressAnimation(inputButtonRef.current);
      }

      // Toggle focus: if already focused, unfocus; otherwise focus this input
      if (onFocusChange) {
        const newFocusState = isInputFocused ? null : inputId;
        onFocusChange(newFocusState);

        // Focus the external HTML input to trigger mobile keyboard
        // iOS Safari requires direct focus from user gesture (no setTimeout)
        if (newFocusState === inputId && window.autoplayInputRefs) {
          const inputRef = window.autoplayInputRefs[inputId];
          if (inputRef?.current) {
            // Set focus maintenance flag BEFORE focusing (critical for iOS)
            if (window.autoplayInputRefs.shouldMaintainFocusRef) {
              window.autoplayInputRefs.shouldMaintainFocusRef.current = true;
            }
            // Claim focus for this tap so the tap-away dismissal (see
            // HiddenAutoplayInputs) doesn't blur the field we just focused.
            window.autoplayInputRefs.focusIntentThisTap = true;

            // iOS sometimes responds better to click() than focus()
            // Try both methods for maximum compatibility
            if (inputRef.current.click) {
              inputRef.current.click();
            }
            inputRef.current.focus();

            // Aggressive refocus strategy for iOS - multiple attempts
            // iOS often blurs immediately, so we need persistent refocusing
            requestAnimationFrame(() => {
              if (
                inputRef?.current &&
                document.activeElement !== inputRef.current
              ) {
                inputRef.current.focus();
              }
            });

            setTimeout(() => {
              if (
                inputRef?.current &&
                document.activeElement !== inputRef.current
              ) {
                inputRef.current.focus();
              }
            }, 10);

            setTimeout(() => {
              if (
                inputRef?.current &&
                document.activeElement !== inputRef.current
              ) {
                inputRef.current.focus();
              }
            }, 50);

            setTimeout(() => {
              if (
                inputRef?.current &&
                document.activeElement !== inputRef.current
              ) {
                inputRef.current.focus();
              }
            }, 100);

            setTimeout(() => {
              if (
                inputRef?.current &&
                document.activeElement !== inputRef.current
              ) {
                inputRef.current.focus();
              }
            }, 200);

            setTimeout(() => {
              if (
                inputRef?.current &&
                document.activeElement !== inputRef.current
              ) {
                inputRef.current.focus();
              }
            }, 300);
          }
        } else if (window.autoplayInputRefs) {
          const inputRef = window.autoplayInputRefs[inputId];
          if (inputRef?.current) {
            // Clear focus maintenance flag when explicitly unfocusing
            if (window.autoplayInputRefs.shouldMaintainFocusRef) {
              window.autoplayInputRefs.shouldMaintainFocusRef.current = false;
            }
            inputRef.current?.blur();
          }
        }
      }
    },
    [isInputFocused, inputId, onFocusChange]
  );

  // Checkbox hover handlers
  const handleCheckboxPointerOver = useCallback(() => {
    if (checkboxBackgroundRef.current) {
      isCheckboxHoveredRef.current = true;
      if (
        !checkboxBackgroundRef.current.filters ||
        checkboxBackgroundRef.current.filters.length === 0
      ) {
        checkboxBackgroundRef.current.filters = [brightnessFilter.current];
      }
    }
  }, []);

  const handleCheckboxPointerOut = useCallback(() => {
    if (checkboxBackgroundRef.current) {
      isCheckboxHoveredRef.current = false;
      checkboxBackgroundRef.current.filters = [];
    }
  }, []);

  // Input button hover handlers
  const handleInputPointerOver = useCallback(() => {
    if (inputButtonRef.current) {
      isInputHoveredRef.current = true;
      if (
        !inputButtonRef.current.filters ||
        inputButtonRef.current.filters.length === 0
      ) {
        inputButtonRef.current.filters = [inputBrightnessFilter.current];
      }
    }
  }, []);

  const handleInputPointerOut = useCallback(() => {
    if (inputButtonRef.current) {
      isInputHoveredRef.current = false;
      inputButtonRef.current.filters = [];
    }
  }, []);

  // Don't render if textures not loaded
  if (
    !checkboxBackgroundTexture ||
    !checkmarkTexture ||
    !inputBackgroundTexture
  ) {
    return null;
  }

  return (
    <Container name="secondary_checkbox_with_input" x={x} y={y}>
      {/* Checkbox Container */}
      <Container
        name="secondary_checkbox"
        eventMode="static"
        cursor="pointer"
        onpointertap={handleCheckboxClick}
        onpointerover={handleCheckboxPointerOver}
        onpointerout={handleCheckboxPointerOut}
      >
        {/* Checkbox Background */}
        <Sprite
          ref={checkboxBackgroundRef}
          texture={checkboxBackgroundTexture}
          name="secondary_checkbox_background"
          anchor={0.5}
          x={0}
          y={0}
          width={checkboxSize}
          height={checkboxSize}
        />

        {/* Checkmark (only visible when checked) */}
        {checked && (
          <Sprite
            ref={checkmarkSpriteRef}
            texture={checkmarkTexture}
            name="secondary_checkmark"
            anchor={0.5}
            x={0}
            y={0}
            width={checkboxSize * 0.65}
            height={checkboxSize * 0.45}
            eventMode="none"
          />
        )}
      </Container>

      {/* Label Text */}
      <Text
        text={displayLabel}
        style={labelStyle}
        anchor={{ x: 0, y: 0.5 }}
        x={labelX}
        y={labelY}
        alpha={checked ? 1.0 : 0.6}
        eventMode="none"
      />

      {/* Input Button */}
      <Container
        name="secondary_input_button"
        x={inputButtonX}
        y={0}
        eventMode="static"
        cursor="pointer"
        onpointertap={handleInputClick}
        onpointerover={handleInputPointerOver}
        onpointerout={handleInputPointerOut}
      >
        <Sprite
          ref={inputButtonRef}
          texture={inputBackgroundTexture}
          name="secondary_input_button_background"
          anchor={0.5}
          width={inputWidth}
          height={inputHeight}
        />

        <Text
          text={isInputFocused ? (value || "") + "|" : value || ""}
          style={inputTextStyle}
          anchor={0.5}
          x={0}
          y={0}
          resolution={2}
          eventMode="none"
        />
      </Container>
    </Container>
  );
};

export default CheckboxWithInput;
